import type { Item } from '@mattoespresso/brew-firestore';

import { useMemo, useContext, createContext } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { query, orderBy, collection } from 'firebase/firestore';

import { FIRESTORE } from 'src/lib/firebase';

const ProductsContext = createContext<{
  data: Item[];
  loading: boolean;
  error: any;
}>({
  data: [],
  loading: false,
  error: null,
});

export function useProducts() {
  return useContext(ProductsContext);
}

export function ProductsProvider({ children }: { children: React.ReactNode }) {
  const [c, loading, error] = useCollection(
    query(collection(FIRESTORE, 'item'), orderBy('display_order', 'asc')),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  const products = useMemo<Item[]>(() => {
    if (loading) {
      return [];
    }

    return c?.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })) as Item[];
  }, [loading, c]);

  const value = useMemo(
    () => ({
      data: products,
      loading,
      error,
    }),
    [products, loading, error]
  );

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
}
